// src/pages/admin/Sidebar.js
import React from 'react';
import { Layout, Menu } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  UserOutlined,
  DashboardOutlined,
  ShopOutlined,
  SettingOutlined,
  FileTextOutlined,
  DollarOutlined,
  LogoutOutlined,
  LockOutlined,
  TagOutlined,
  TeamOutlined,
  GiftOutlined,
  BarChartOutlined,
  MessageOutlined,
  FileOutlined,
  BookOutlined,    // For Courses
  FlagOutlined,    // For Challenges
  ProjectOutlined, // For Projects
  SolutionOutlined // For Opportunities (Jobs & Internships)
} from '@ant-design/icons';

const { Sider } = Layout;
const { SubMenu } = Menu;

const Sidebar = ({ collapsed, setCollapsed, handleMenuClick }) => {
  const navigate = useNavigate();

  // Fallback handler if handleMenuClick isn't provided
  const defaultHandleMenuClick = ({ key }) => {
    if (key === 'logout') {
      console.log('Logout clicked');
      navigate('/'); // Placeholder; replaced by onLogout in AdminLayout
    } else {
      navigate(`/admin/${key}`);
    }
  };

  const onMenuClick = handleMenuClick || defaultHandleMenuClick;

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
      width={250}
      style={{ background: '#001529' }}
    >
      <div
        style={{
          height: 32,
          margin: 16,
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: 4,
        }}
      />
      <Menu
        theme="dark"
        defaultSelectedKeys={['dashboard']}
        mode="inline"
        onClick={onMenuClick}
      >
        {/* Priority Items */}
        <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
          Dashboard
        </Menu.Item>
        <SubMenu key="categories" icon={<TagOutlined />} title="Categories">
          <Menu.Item key="categories-manage">Manage Categories</Menu.Item>
          <Menu.Item key="product_categories">Products Categories</Menu.Item>
        </SubMenu>

        <SubMenu key="courses" icon={<BookOutlined />} title="Courses">
          <Menu.Item key="courses-manage">Manage Courses</Menu.Item>
        </SubMenu>
        <SubMenu key="challenges" icon={<FlagOutlined />} title="Challenges">
          <Menu.Item key="challenges-manage">Manage Challenges</Menu.Item>
        </SubMenu>
        <SubMenu key="projects" icon={<ProjectOutlined />} title="Projects">
          <Menu.Item key="projects-manage">Manage Projects</Menu.Item>
        </SubMenu>
        {/* Role-Specific Features */}
        <SubMenu key="student" icon={<UserOutlined />} title="Students">
          <Menu.Item key="student-profiles">Profiles</Menu.Item>
          <Menu.Item key="student-enrollments">Course Enrollments</Menu.Item>
          <Menu.Item key="student-assessments">Assessments</Menu.Item>
          <Menu.Item key="student-challenge-submissions">Challenge Submissions</Menu.Item>
          <Menu.Item key="student-resources">Learning Resources</Menu.Item>
          <Menu.Item key="student-modules">Learning Modules</Menu.Item>
          <Menu.Item key="student-job-applications">Job Applications</Menu.Item>
          <Menu.Item key="student-internship-applications">Internship Applications</Menu.Item>
          <Menu.Item key="student-donations">Donations</Menu.Item>
          <Menu.Item key="student-notifications">Notifications</Menu.Item>
          <Menu.Item key="student-reviews">Reviews</Menu.Item>
          <Menu.Item key="student-skills">User Skills</Menu.Item>
          <Menu.Item key="student-messages">Messages</Menu.Item>
          <Menu.Item key="student-ebooks">Ebooks</Menu.Item>
          <Menu.Item key="student-case-studies">Case Studies</Menu.Item>
          <Menu.Item key="student-community">Community</Menu.Item>
          <Menu.Item key="student-community-members">Community Members</Menu.Item>
          <Menu.Item key="student-discussions">Discussions</Menu.Item>
          <Menu.Item key="student-discussion-replies">Discussion Replies</Menu.Item>
        </SubMenu>
        <SubMenu key="mentor" icon={<TeamOutlined />} title="Mentors">
          <Menu.Item key="mentor-mentorships">Mentorships</Menu.Item>
          <Menu.Item key="mentor-assessments">Assessments</Menu.Item>
          <Menu.Item key="mentor-resources">Learning Resources</Menu.Item>
          <Menu.Item key="mentor-modules">Learning Modules</Menu.Item>
          <Menu.Item key="mentor-messages">Messages</Menu.Item>
          <Menu.Item key="mentor-discussions">Discussions</Menu.Item>
          <Menu.Item key="mentor-discussion-replies">Discussion Replies</Menu.Item>
        </SubMenu>
        <SubMenu key="opportunities" icon={<SolutionOutlined />} title="Opportunities">
          <Menu.Item key="opportunities-jobs">Manage Jobs</Menu.Item>
          <Menu.Item key="opportunities-internships">Manage Internships</Menu.Item>
        </SubMenu>

        {/* Business & Content */}
        <SubMenu key="vendors" icon={<ShopOutlined />} title="Vendors">
          <Menu.Item key="vendors-manage">Manage Vendors</Menu.Item>
          <Menu.Item key="vendors-products">Products</Menu.Item>
          <Menu.Item key="vendors-transactions">Transactions</Menu.Item>
          <Menu.Item key="vendors-approvals">Approvals</Menu.Item>
        </SubMenu>
        <SubMenu key="posts" icon={<MessageOutlined />} title="Posts">
          <Menu.Item key="posts-manage">Manage Posts</Menu.Item>
          <Menu.Item key="tags">Manage Tags</Menu.Item>
        </SubMenu>
        <SubMenu key="campaigns" icon={<GiftOutlined />} title="Campaigns">
          <Menu.Item key="campaigns-manage">Manage Campaigns</Menu.Item>
        </SubMenu>
        <SubMenu key="partnerships" icon={<TeamOutlined />} title="Partnerships">
          <Menu.Item key="partnerships-manage">Manage Partnerships</Menu.Item>
        </SubMenu>

        {/* Learning Support */}
        <SubMenu key="themes" icon={<FileOutlined />} title="Themes">
        <Menu.Item key="themes-categories">Theme Categories</Menu.Item>
          <Menu.Item key="themes-manage">Manage Themes</Menu.Item>
        </SubMenu>
        <SubMenu key="certificates" icon={<FileTextOutlined />} title="Certificates">
          <Menu.Item key="certificates-manage">Manage Certificates</Menu.Item>
        </SubMenu>
        <SubMenu key="transactions" icon={<DollarOutlined />} title="Transactions">
          <Menu.Item key="transactions-all">All Transactions</Menu.Item>
        </SubMenu>

        {/* Analytics & Shared */}
        <SubMenu key="reports" icon={<BarChartOutlined />} title="Reports">
          <Menu.Item key="reports-users">User Activity</Menu.Item>
          <Menu.Item key="reports-vendors">Vendor Performance</Menu.Item>
          <Menu.Item key="reports-financial">Financial Overview</Menu.Item>
        </SubMenu>
        <SubMenu key="shared" icon={<TagOutlined />} title="Shared Features">
          <Menu.Item key="entity-tags">Entity Tags</Menu.Item>
        </SubMenu>
        <SubMenu key="users" icon={<UserOutlined />} title="Users">
          <Menu.Item key="users">Manage Users</Menu.Item>
        </SubMenu>

        {/* System & Settings */}
        <SubMenu key="settings" icon={<SettingOutlined />} title="Settings">
          <Menu.Item key="settings-general">General Settings</Menu.Item>
          <Menu.Item key="system-users">System Users</Menu.Item>
          <Menu.Item key="roles">Manage Roles</Menu.Item>
        </SubMenu>
        <SubMenu key="system" icon={<LockOutlined />} title="System">
          <Menu.Item key="system-login-activity">Login Activity</Menu.Item>
          <Menu.Item key="system-activity-logs">Activity Logs</Menu.Item>
          <Menu.Item key="system-backups">Backups</Menu.Item>
          <Menu.Item key="system-api-usage">API Usage</Menu.Item>
          <Menu.Item key="system-error-logs">Error Logs</Menu.Item>
          <Menu.Item key="system-config">System Configuration</Menu.Item>
        </SubMenu>

        <Menu.Item key="logout" icon={<LogoutOutlined />}>
          Logout
        </Menu.Item>
      </Menu>
    </Sider>
  );
};

export default Sidebar;